import { PlusOutlined } from '@ant-design/icons';
import { Button, Input, Space, Table, Divider, Spin, Tag, Typography } from 'antd';
import moment from 'moment';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { t } from 'ttag';

import ShowMore from 'common/components/ShowMore';
import useVisibleColumns from 'common/components/Table/useVisibleColumns';
import useSyncWithUrlSearchParams from 'common/hooks/useSyncWithUrlSearchParams';
import { useTableQuery } from 'common/hooks/useTableQuery';

import { usePackagesQuery } from '../Packages/queries';

import CreateModal from './CreateModal';
import ExtendModal from './ExtendModal';
import { TrialsListQueryKey } from './queries';
import UpgradeModal from './UpgradeModal';

const { Search } = Input;

const STATUS = {
  ACTIVE: 'active',
  EXPIRED: 'expired',
  UPGRADED: 'upgraded',
  UNUSED: 'unused',
};

const STATUS_LABELS = () => ({
  [STATUS.ACTIVE]: t`Active`,
  [STATUS.EXPIRED]: t`Expired`,
  [STATUS.UPGRADED]: t`Upgraded`,
  [STATUS.UNUSED]: t`Unused`,
});

const SearchBox = styled(Search)`
  && {
    width: 350px;
  }
`;

const Trials = () => {
  const [query] = useSyncWithUrlSearchParams();
  const [search, setSearch] = useState(query.search || '');
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [trialToExtend, setTrialToExtend] = useState(null);
  const [trialToUpgrade, setTrialToUpgrade] = useState(null);
  const { data: packages, isLoading: packagesLoading } = usePackagesQuery({
    type: 'event_credit,user_credit',
  });

  const columns = useVisibleColumns(
    [
      {
        key: 'eventShortcode',
        dataIndex: 'eventShortcode',
        title: t`Event Code`,
        width: 150,
        render: shortcode => (
          <Typography.Text code style={{ wordBreak: 'keep-all' }}>
            {shortcode}
          </Typography.Text>
        ),
      },
      {
        key: 'eventName',
        dataIndex: 'eventName',
        title: t`Event Name`,
        width: 200,
      },
      {
        key: 'organizationName',
        dataIndex: 'organizationName',
        title: t`Organization`,
        width: 200,
        render: (orgName, trial) => (
          <Link
            to={`/organization/${trial.organizationId}/events`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {orgName}
          </Link>
        ),
      },
      {
        key: 'organizerEmails',
        dataIndex: 'organizerEmails',
        title: t`Organizer Emails`,
        width: 250,
        render: emails => (
          <ShowMore
            items={emails.map(email => ({ id: email, name: email }))}
            isTag
            uppercase={false}
          />
        ),
      },
      {
        key: 'blueprintId',
        dataIndex: 'blueprintId',
        title: t`Package`,
        width: 150,
        render: blueprintId => {
          const packageObj = packages?.find(pkg => pkg.id === blueprintId);
          if (packagesLoading) {
            return <Spin size="small" />;
          }
          if (!packageObj) {
            return '-';
          }
          return <Tag color="default">{packageObj.name}</Tag>;
        },
      },
      {
        key: 'createdAt',
        dataIndex: 'createdAt',
        title: t`Created at`,
        width: 130,
        sorter: true,
        render: date => moment(date).format('MM/DD/YYYY'),
      },
      {
        key: 'status',
        dataIndex: 'status',
        title: t`Status`,
        width: 150,
        render: (status, trial) => {
          if (status === STATUS.ACTIVE && trial.daysRemaining != null) {
            const { daysRemaining } = trial;
            return `${daysRemaining} day(s) remaining`;
          }
          return STATUS_LABELS()[status] ?? t`Unknown status (${status})`;
        },
      },
      {
        key: 'actions',
        title: t`Actions`,
        width: 250,
        render: (_, trial) => {
          const items = [];

          items.push(
            <Link to={`/organization/${trial.organizationId}/event/${trial.eventId}`}>
              {t`EXP`}
            </Link>,
          );
          const canUpgradeOrExtend =
            trial.status !== STATUS.UPGRADED && trial.status !== STATUS.UNUSED;

          if (canUpgradeOrExtend) {
            items.push(
              <Typography.Link
                key="upgrade"
                type="link"
                size="small"
                onClick={() => setTrialToUpgrade(trial)}
              >
                {t`Upgrade`}
              </Typography.Link>,
            );
            items.push(
              <Typography.Link
                key="extend"
                type="link"
                size="small"
                onClick={() => setTrialToExtend(trial)}
              >
                {t`Extend`}
              </Typography.Link>,
            );
          }

          return <Space split={<Divider type="vertical" />}>{items}</Space>;
        },
      },
    ],
    {
      defaultVisibleColumns: [
        'eventShortcode',
        'organizationName',
        'eventName',
        'createdAt',
        'status',
      ],
      omitColumns: ['actions'],
      disabledColumns: ['eventShortcode', 'organizationName', 'eventName'],
    },
  );

  const { tableProps } = useTableQuery(
    apiParams => ({
      queryKey: TrialsListQueryKey.getQueryKey(search, apiParams),
      queryFn: TrialsListQueryKey.queryFn,
    }),
    {
      columns,
      rowKey: 'id',
    },
    search,
  );

  return (
    <>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBlock: 20 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsCreateModalVisible(true)}
        >
          {t`New Trial`}
        </Button>
        <SearchBox
          defaultValue={search}
          placeholder={t`Search Trials...`}
          disabled={tableProps.loading}
          onSearch={value => setSearch(value)}
        />
      </Space>

      <Table {...tableProps} scroll={{ x: 'max-content' }} />

      <CreateModal visible={isCreateModalVisible} onClose={() => setIsCreateModalVisible(false)} />

      {trialToExtend && (
        <ExtendModal trial={trialToExtend} onClose={() => setTrialToExtend(null)} />
      )}

      {trialToUpgrade && (
        <UpgradeModal trial={trialToUpgrade} onClose={() => setTrialToUpgrade(null)} />
      )}
    </>
  );
};

export default Trials;
